import React, { useState, useCallback } from 'react';
import { Card, CardActionArea, Box, Typography, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText } from '@mui/material';
import { AccessTime, LocationOn, Description, Warning, Commute, Delete as DeleteIcon } from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import api from '../../api/axios';

// Status colors matching the design guide
const STATUS_COLORS = {
  'New Request': '#64748B', // Slate
  'Needs Info': '#9CA3AF',  // Grey
  'Tentative': '#3B82F6',   // Blue
  'Locked': '#EAB308',      // Yellow
  'Pending': '#EAB308',     // Yellow
  'Confirmed': '#22C55E',   // Green
  'Scheduled': '#3B82F6',   // Blue (legacy/import)
  'Failed': '#EF4444',      // Red
  'Completed': '#8B5CF6',   // Purple
  'Waiting on Client': '#F97316', // Orange
  'En Route': '#06B6D4',    // Cyan
  'On Site': '#4ADE80',     // Light Green
  'Rescheduled': '#F97316', // Orange
  'Cancelled': '#9CA3AF',   // Grey
  'Invoice Sent': '#A78BFA',// Light Purple
  'Invoiced': '#8B5CF6'     // Purple
};

const JobCard = ({ job, onClick, onDeleted }) => {
  const statusColor = STATUS_COLORS[job.status] || '#9CA3AF';
  const [contextMenu, setContextMenu] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(
      contextMenu === null
        ? { mouseX: e.clientX - 2, mouseY: e.clientY - 4 }
        : null
    );
  }, [contextMenu]);

  const handleCloseMenu = () => setContextMenu(null);

  const handleDeleteClick = () => {
    handleCloseMenu();
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/jobs/${job.id}`);
      if (onDeleted) onDeleted(job.id);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete job';
      alert(msg);
    }
    setDeleting(false);
    setDeleteDialogOpen(false);
  };
  
  // Calculate slipping status (Active jobs > 7 days old)
  const isSlipping = React.useMemo(() => {
    if (!job.created_at) return false;
    const isActive = !['Completed', 'Failed', 'Cancelled', 'completed', 'failed', 'cancelled'].includes(job.status);
    const age = differenceInDays(new Date(), new Date(job.created_at));
    return isActive && age > 7;
  }, [job]);
  
  // Format time if available
  let timeDisplay = 'Unscheduled';
  if (job.scheduled_time) {
    timeDisplay = format(new Date(`1970-01-01T${job.scheduled_time}`), 'h:mm a');
  } else if (job.scheduled_date) {
    // scheduled_date is a date/time; if it looks like midnight, no specific time was set
    const d = new Date(job.scheduled_date);
    if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
      timeDisplay = 'TBD';
    } else {
      timeDisplay = format(d, 'h:mm a');
    }
  } else if (job.status === 'Needs Info') {
    timeDisplay = 'Needs Info';
  } else if (job.status === 'Tentative') {
    timeDisplay = 'Tentative';
  }

  return (
    <>
    <Card 
      elevation={0}
      onContextMenu={handleContextMenu}
      sx={{ 
        mb: 1.5, 
        border: '1px solid #E2E8F0',
        borderLeft: `4px solid ${statusColor}`,
        borderRadius: 2,
        transition: 'transform 0.1s, box-shadow 0.1s',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        }
      }}
    >
      <CardActionArea 
        onClick={() => onClick(job)}
        sx={{ p: 2, display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {/* Top Row: Time and Status Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500 }}>
              <AccessTime sx={{ fontSize: 16, mr: 0.5 }} />
              {timeDisplay}
            </Box>
            {isSlipping && (
              <Chip
                icon={<Warning style={{ fontSize: 14 }} />}
                label="Slipping"
                size="small"
                color="error"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', border: '1px solid', fontWeight: 700 }}
              />
            )}
          </Box>

          {/* Address - Prominent */}
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3 }}>
            {job.location || "No Address Provided"}
          </Typography>

          {/* Job Type and Client */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {job.description} • {job.client_name || "Unknown Client"}
          </Typography>

          {/* Indicators row (optional: files, warnings) */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {job.estimated_travel_time_mins && (
              <Chip 
                icon={<Commute sx={{ fontSize: 14 }} />}
                label={`${job.estimated_travel_time_mins}m travel`} 
                size="small" 
                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#F1F5F9', border: 'none' }} 
              />
            )}
            {job.info_status && job.info_status !== 'complete' && (
              <Chip 
                label={job.info_status} 
                size="small" 
                variant="outlined" 
                color="warning" 
                sx={{ height: 20, fontSize: '0.7rem' }} 
              />
            )}
          </Box>
        </Box>
      </CardActionArea>
    </Card>

    {/* Right-click context menu */}
    <Menu
      open={contextMenu !== null}
      onClose={handleCloseMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
        <ListItemIcon>
          <DeleteIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete Job</ListItemText>
      </MenuItem>
    </Menu>

    {/* Delete confirmation dialog */}
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
      <DialogTitle>Delete Job #{job.id}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to permanently delete this job at <strong>{job.location || 'Unknown'}</strong>?
          This action cannot be undone.
          {job.status === 'Completed' && ' Consider cancelling instead if you want to keep the record.'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
        <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default JobCard;
